import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { PageHeader, SectionCard, Stat, Spinner } from '../components/ui';
import { fmtMoney, fmtNumber } from '../lib/format';

interface Summary {
  totalUsers: number;
  onlineUsers: number;
  totalRooms: number;
  totalRake: number;
  crmUsersByRole: Record<string, number>;
}

export function DashboardPage() {
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get<Summary>('/api/crm/dashboard/summary')
        .then((d) => alive && setData(d))
        .finally(() => alive && setLoading(false));
    load();
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div>
      <PageHeader
        title="Дашборд"
        subtitle="Сводка по клубу в реальном времени"
        actions={
          <span className="text-xs text-stone-500 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            auto-refresh
          </span>
        }
      />
      {loading || !data ? (
        <div className="flex items-center gap-2 text-stone-400 text-sm"><Spinner /> Загрузка…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <Stat label="Игроков всего" value={fmtNumber(data.totalUsers)} icon={<Icon.Users size={14} />} tone="sky"
              hint={<span className="text-emerald-300">Онлайн: {fmtNumber(data.onlineUsers)}</span>} />
            <Stat label="Комнат" value={fmtNumber(data.totalRooms)} icon={<Icon.Rooms size={14} />} tone="amber" />
            <Stat label="Заработано рейка" value={fmtMoney(data.totalRake)} icon={<Icon.Cash size={14} />} tone="green" hint="за всё время" />
            <Stat label="Сотрудников CRM" value={fmtNumber((data.crmUsersByRole.super_admin ?? 0) + (data.crmUsersByRole.admin ?? 0) + (data.crmUsersByRole.dealer_manager ?? 0) + (data.crmUsersByRole.viewer ?? 0))} icon={<Icon.Crown size={14} />} tone="stone" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4 mt-4">
            <SectionCard title="Состав CRM-команды" className="lg:col-span-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(['super_admin', 'admin', 'dealer_manager', 'viewer'] as const).map((r) => (
                  <div key={r} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <div className="text-[11px] uppercase tracking-wider text-stone-500">{roleLabel(r)}</div>
                    <div className="text-2xl font-bold mt-1">{fmtNumber(data.crmUsersByRole[r] ?? 0)}</div>
                  </div>
                ))}
              </div>
            </SectionCard>
            <SectionCard title="Быстрые действия">
              <div className="space-y-2">
                <QuickAction to="/users" label="Пользователи" icon={<Icon.Users size={14} />} />
                <QuickAction to="/rooms" label="Комнаты" icon={<Icon.Rooms size={14} />} />
                <QuickAction to="/earnings" label="Заработок" icon={<Icon.Cash size={14} />} />
                <QuickAction to="/audit" label="Аудит" icon={<Icon.Shield size={14} />} />
              </div>
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}

function QuickAction({ to, label, icon }: { to: string; label: string; icon: React.ReactNode }) {
  return (
    <a
      href={to}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.025] hover:bg-white/[0.06] border border-white/10 transition-colors"
    >
      <span className="w-7 h-7 rounded-lg bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20 flex items-center justify-center">
        {icon}
      </span>
      <span className="text-sm font-medium">{label}</span>
      <Icon.Chevron size={14} className="ml-auto text-stone-500" />
    </a>
  );
}

function roleLabel(r: string): string {
  switch (r) {
    case 'super_admin': return 'Суперадмин';
    case 'admin': return 'Администратор';
    case 'dealer_manager': return 'Менеджер крупье';
    case 'viewer': return 'Наблюдатель';
    default: return r;
  }
}
